// hooks/useUserAvailability.ts

import { useQuery, useQueryClient } from "@tanstack/react-query";
import useAuth from "@/modules/common/hooks/auth.hook";
import { getUserAvailabilityV2 } from "@/modules/common/api/index.v2";
import { updateUserConfig } from "@/modules/agents/api/index.v2";

export const useUserAvailability = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const { logout, user } = useAuth();

  const { data: availabilityData } = useQuery(
    ["getUserAvailability"],
    () => getUserAvailabilityV2(user?.["cognito:username"]),
    {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      onError: (error) => {
        console.error("Failed to fetch user availability:", error);
      },
      enabled,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: Infinity,
      refetchInterval: 30000,
    }
  );
  const { isAvailable = false, autodialerState } = availabilityData?.data ?? {};
  const isAutodialerActive = autodialerState?.active ?? false;

  const toggleUserAvailability = async (newStatus: boolean) => {
    try {
      await updateUserConfig(user?.["cognito:username"], {
        isAvailable: newStatus,
      });
      queryClient.invalidateQueries({ queryKey: ["getUserAvailability"] });
      queryClient.invalidateQueries({ queryKey: ["getActivityUsers"] });
    } catch (error) {
      console.error("Failed to update user availability:", error);
    }
  };

  return {
    isAvailable,
    isAutodialerActive,
    toggleUserAvailability,
    logout,
    availabilityData,
  };
};
